import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

import { namedOperations } from '@sorare/core/src/__generated__/operations';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';

import type {
  RemoveFromWatchlistMutation,
  RemoveFromWatchlistMutationVariables,
} from './__generated__/useRemoveFromWatchlist.graphql.ts';

const REMOVE_FROM_WATCHLIST_MUTATION = gql`
  mutation RemoveFromWatchlistMutation(
    $watchlistId: ID!
    $printablePlayerIds: [ID!]!
  ) {
    removeFromWatchlist(
      input: {
        watchlistId: $watchlistId
        printablePlayerIds: $printablePlayerIds
      }
    ) {
      watchlist {
        slug
        id
        title
        commonPlayers(first: 7) {
          nodes {
            id
            anyPlayer {
              slug
            }
          }
        }
        totalPlayersCount
      }
    }
  }
` as TypedDocumentNode<
  RemoveFromWatchlistMutation,
  RemoveFromWatchlistMutationVariables
>;

export const useRemoveFromWatchlist = () => {
  const [mutate, { loading }] = useMutation(REMOVE_FROM_WATCHLIST_MUTATION, {
    showErrorsWithSnackNotification: true,
  });

  const removeFromWatchlist = useCallback(
    async (watchlistId: string, printablePlayerIds: string[]) =>
      mutate({
        variables: { watchlistId, printablePlayerIds },
        refetchQueries: [
          namedOperations.Query.AdvancedWatchlistSearchQuery,
          namedOperations.Query.AdvancedWatchlistFacetSearchQuery,
        ],
      }),
    [mutate]
  );

  return {
    removeFromWatchlist: {
      mutate: removeFromWatchlist,
      loading,
    },
  };
};
